import { useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { X, Download, FileSpreadsheet, Upload, Loader2, AlertTriangle, CheckCircle2, Copy } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const MAX_PREVIEW_ROWS = 300;

/**
 * BLOQUE 6 PARTE B — Modal de carga masiva determinista (SIN IA) por plantilla Excel
 * oficial, reutilizable para Materiales y Herramientas.
 */
export function ImportExcelTemplateModal({ type, open, onClose, onConfirmed }) {
  const [step, setStep] = useState("select"); // select | preview | done
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null); // { valid_items, errors, summary, file_name }
  const [items, setItems] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);

  const isMaterials = type === "materials";
  const title = isMaterials ? "Importar Materiales desde Plantilla Excel" : "Importar Herramientas desde Plantilla Excel";
  const templateUrl = `${API}/inventory/${isMaterials ? "materials" : "tools"}/template`;
  const previewEndpoint = `${API}/inventory/${isMaterials ? "materials" : "tools"}/import-excel`;
  const confirmEndpoint = `${previewEndpoint}/confirm`;
  const dupField = isMaterials ? "duplicate_material_id" : "duplicate_tool_id";
  const dupNameField = isMaterials ? "duplicate_material_name" : "duplicate_tool_name";
  const dupStockField = isMaterials ? "duplicate_material_stock" : null;
  const sumAction = isMaterials ? "sum_existing" : "update_existing";

  const reset = () => { setStep("select"); setFile(null); setPreview(null); setItems([]); setResult(null); };
  const handleClose = () => { reset(); onClose(); };

  const handleDownloadTemplate = async () => {
    try {
      const { data } = await axios.get(templateUrl, { withCredentials: true, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = isMaterials ? "plantilla_materiales.xlsx" : "plantilla_herramientas.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error("Error al descargar la plantilla"); }
  };

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", f);
      const { data } = await axios.post(previewEndpoint, formData, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      setItems(data.valid_items || []);
      setStep("preview");
    } catch (err) { toast.error(err.response?.data?.detail || "Error al procesar el archivo"); }
    finally { setLoading(false); }
  };

  const toggleSelected = (idx) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, selected: !it.selected } : it));
  const toggleAction = (idx) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, action: it.action === sumAction ? "create_new" : sumAction } : it));

  const handleConfirm = async () => {
    const selected = items.filter(it => it.selected);
    if (selected.length === 0) { toast.error("Seleccioná al menos un ítem para importar"); return; }
    setConfirming(true);
    try {
      const { data } = await axios.post(confirmEndpoint, { items: selected, file_name: preview?.file_name || "" }, { withCredentials: true });
      setResult(data);
      setStep("done");
      toast.success(data.message);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al confirmar la importación"); }
    finally { setConfirming(false); }
  };

  const handleFinish = () => { onConfirmed(); handleClose(); };

  if (!open) return null;

  const selectedCount = items.filter(it => it.selected).length;
  const duplicatesCount = items.filter(it => it[dupField]).length;
  const visibleItems = items.slice(0, MAX_PREVIEW_ROWS);

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4" data-testid="import-excel-template-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <FileSpreadsheet size={18} className="text-emerald-600" />
            <h2 className="text-base font-bold text-gray-900">{title}</h2>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600" data-testid="close-import-excel-modal"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-5">
          {step === "select" && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Descargá la plantilla oficial, completá los datos y volvé a subirla. Sin IA — validación determinista, apta para 1.000+ filas.</p>
              <button onClick={handleDownloadTemplate} className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-[#2E6DA4]/40 text-[#2E6DA4] hover:bg-[#2E6DA4]/5 rounded-xl py-4 text-sm font-semibold transition-all" data-testid="download-template-btn">
                <Download size={16} /> Descargar Plantilla ({isMaterials ? "Materiales" : "Herramientas"})
              </button>
              <label className={`w-full flex items-center justify-center gap-2 border-2 border-dashed rounded-xl py-6 text-sm font-semibold transition-all cursor-pointer ${loading ? "border-gray-200 text-gray-400" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`}>
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                {loading ? "Procesando archivo..." : "Subir Excel completado (.xlsx)"}
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFileChange} disabled={loading} data-testid="import-excel-file-input" />
              </label>
            </div>
          )}

          {step === "preview" && preview && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-700">{preview.summary.valid}</p>
                  <p className="text-[11px] text-emerald-600 font-medium">Filas válidas</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-red-700">{preview.summary.errors}</p>
                  <p className="text-[11px] text-red-600 font-medium">Con error</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-amber-700">{duplicatesCount}</p>
                  <p className="text-[11px] text-amber-600 font-medium">Duplicadas</p>
                </div>
              </div>

              {preview.errors?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 max-h-32 overflow-y-auto" data-testid="import-excel-errors">
                  {preview.errors.map((e, i) => (
                    <p key={i} className="text-xs text-red-700 flex items-start gap-1.5"><AlertTriangle size={11} className="flex-shrink-0 mt-0.5" /> {e.message}</p>
                  ))}
                </div>
              )}

              {items.length > 0 && (
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="max-h-80 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="p-2 text-left w-8"></th>
                          <th className="p-2 text-left">Nombre</th>
                          {isMaterials && <th className="p-2 text-left">Marca</th>}
                          <th className="p-2 text-left">Categoría</th>
                          <th className="p-2 text-left">{isMaterials ? "Cant." : "Valor"}</th>
                          <th className="p-2 text-left">Se aplicará a</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleItems.map((it, idx) => (
                          <tr key={idx} className={`border-t border-gray-100 ${it[dupField] ? "bg-amber-50/50" : ""}`} data-testid={`import-row-${idx}`}>
                            <td className="p-2"><input type="checkbox" checked={it.selected} onChange={() => toggleSelected(idx)} data-testid={`import-row-checkbox-${idx}`} /></td>
                            <td className="p-2 font-medium text-gray-800">{it.name}</td>
                            {isMaterials && <td className="p-2 text-gray-500">{it.marca || "—"}</td>}
                            <td className="p-2 text-gray-500">{it.category}</td>
                            <td className="p-2 text-gray-500">{isMaterials ? `${it.quantity} ${it.unit}` : it.value_pyg?.toLocaleString("es-PY")}</td>
                            <td className="p-2">
                              {it[dupField] ? (
                                <button type="button" onClick={() => toggleAction(idx)} className={`flex items-center gap-1 px-2 py-1 rounded-lg font-semibold ${it.action === sumAction ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`} data-testid={`import-row-action-${idx}`}>
                                  <Copy size={10} />
                                  {it.action === sumAction ? `Sumar a "${it[dupNameField]}"${dupStockField ? ` (stock: ${it[dupStockField]})` : ""}` : "Crear nuevo (duplicado)"}
                                </button>
                              ) : (
                                <span className="flex items-center gap-1 text-emerald-700 font-medium"><CheckCircle2 size={11} /> Crear nuevo</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {items.length > MAX_PREVIEW_ROWS && (
                    <p className="text-[11px] text-gray-400 text-center py-2 bg-gray-50">Mostrando las primeras {MAX_PREVIEW_ROWS} de {items.length} filas válidas. Todas se incluirán al confirmar.</p>
                  )}
                </div>
              )}

              <div className="flex gap-3">
                <button onClick={() => setStep("select")} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Volver</button>
                <button onClick={handleConfirm} disabled={confirming || selectedCount === 0} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="confirm-import-excel-btn">
                  {confirming ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
                  {confirming ? "Importando..." : `Confirmar Importación (${selectedCount})`}
                </button>
              </div>
            </div>
          )}

          {step === "done" && result && (
            <div className="space-y-4 text-center py-4">
              <CheckCircle2 size={40} className="text-emerald-500 mx-auto" />
              <p className="text-sm text-gray-700">{result.message}</p>
              <div className="flex gap-3 justify-center text-sm">
                <span className="bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-lg font-semibold">{result.created_count} creados</span>
                <span className="bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg font-semibold">{result.updated_count} actualizados</span>
              </div>
              <button onClick={handleFinish} className="w-full bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold" data-testid="finish-import-excel-btn">Listo</button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
